import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { api, ApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "../components/ui/card";
import { useToast } from "../components/ui/toast";

function clearWazzupResultParams() {
  const url = new URL(window.location.href);
  url.searchParams.delete("wazzup");
  url.searchParams.delete("wauth_state");
  window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
}

function notifyOpenerAndClose() {
  if (!window.opener) return;
  window.opener.postMessage({ type: "AI_CRM_WAZZUP_CONNECTED" }, "*");
  window.setTimeout(() => window.close(), 600);
}

export function WazzupSettingsPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [apiKey, setApiKey] = useState("");
  const popupRef = useRef<Window | null>(null);
  const handledCallbackRef = useRef(false);

  const status = useQuery({
    queryKey: ["wazzup-status"],
    queryFn: api.getWazzupStatus,
  });

  const refreshWazzupQueries = () => {
    queryClient.invalidateQueries({ queryKey: ["wazzup-status"] });
    queryClient.invalidateQueries({ queryKey: ["wazzup-channels"] });
  };

  const connect = useMutation({
    mutationFn: api.getWazzupConnectUrl,
    onSuccess: (data) => {
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.location.href = data.url;
        popupRef.current.focus();
        return;
      }
      window.open(data.url, "_blank", "noopener,noreferrer");
    },
    onError: (err) => {
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
      const message =
        err instanceof ApiError ? err.message : "Не удалось открыть подключение Wazzup";
      toast(message, "destructive");
    },
  });

  const complete = useMutation({
    mutationFn: api.completeWazzupAuth,
    onSuccess: (data) => {
      toast(`Wazzup подключён · каналов: ${data.channelCount ?? 0}`, "success");
      refreshWazzupQueries();
      clearWazzupResultParams();
      notifyOpenerAndClose();
    },
    onError: (err) => {
      const message =
        err instanceof ApiError ? err.message : "Не удалось завершить подключение Wazzup";
      toast(message, "destructive");
      clearWazzupResultParams();
    },
  });

  const save = useMutation({
    mutationFn: () => api.saveWazzupKey(apiKey),
    onSuccess: (data) => {
      toast(`Wazzup подключён · каналов: ${data.channelCount ?? 0}`, "success");
      setApiKey("");
      refreshWazzupQueries();
    },
    onError: (err) => {
      const message =
        err instanceof ApiError ? err.message : "Не удалось сохранить ключ";
      toast(message, "destructive");
    },
  });

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (popupRef.current && event.source !== popupRef.current) return;

      const data = event.data as { type?: unknown };
      if (data?.type !== "AI_CRM_WAZZUP_CONNECTED") return;

      toast("Wazzup подключён", "success");
      refreshWazzupQueries();
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  });

  useEffect(() => {
    if (handledCallbackRef.current) return;

    const params = new URLSearchParams(window.location.search);
    const result = params.get("wazzup");
    const wauthState = params.get("wauth_state");

    if (result === "connected") {
      handledCallbackRef.current = true;
      toast("Wazzup подключён", "success");
      refreshWazzupQueries();
      clearWazzupResultParams();
      notifyOpenerAndClose();
      return;
    }

    if (result === "error") {
      handledCallbackRef.current = true;
      toast("Не удалось подключить Wazzup", "destructive");
      clearWazzupResultParams();
      return;
    }

    if (wauthState) {
      handledCallbackRef.current = true;
      complete.mutate(wauthState);
    }
  });

  const connected = status.data?.connected ?? false;

  const handleConnect = () => {
    popupRef.current = window.open(
      "",
      "wazzup-connect",
      "width=760,height=760,menubar=no,toolbar=no,location=no,status=no",
    );
    connect.mutate();
  };

  return (
    <div className="mx-auto max-w-xl space-y-4 p-4">
      <Link
        to="/settings"
        className="inline-flex items-center text-sm text-muted-foreground"
      >
        <ArrowLeft className="mr-1 h-4 w-4" /> Назад
      </Link>

      <Card>
        <CardHeader>
          <CardTitle>Подключение Wazzup</CardTitle>
          <CardDescription>
            Подключите кабинет Wazzup кнопкой. Мы сохраним ключ зашифрованным и
            сразу подпишем сервер на входящие сообщения.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {connected && (
            <p className="text-sm text-green-600">
              Wazzup подключён
            </p>
          )}
          {/* <Button
            onClick={handleConnect}
            disabled={connect.isPending || complete.isPending}
            className="w-full"
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            {connect.isPending ? "Открываем..." : connected ? "Переподключить Wazzup" : "Подключить Wazzup"}
          </Button> */}

          <details className="rounded-md border p-3">
            <summary className="cursor-pointer text-sm font-medium">
              Подключить вручную API-ключом
            </summary>
            <div className="mt-3 space-y-3">
              <div className="space-y-1">
                <Label htmlFor="apiKey">
                  {connected ? "Изменить ключ" : "API-ключ"}
                </Label>
                <Input
                  id="apiKey"
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="••••••••••••••••"
                />
              </div>
              <Button
                variant="secondary"
                onClick={() => save.mutate()}
                disabled={save.isPending || apiKey.trim().length === 0}
              >
                {save.isPending ? "Проверяем..." : "Сохранить ключ"}
              </Button>
            </div>
          </details>
        </CardContent>
      </Card>
    </div>
  );
}
